import React from "react";
import { useFela } from "react-fela";
import { useInView } from "react-intersection-observer";

import { Grid } from "@/components/Grid";
import { useTheme } from "@/hooks/useTheme";
import { Card } from "@/components/Card";
import { Heading } from "@/components/Heading";
import { LinkButton } from "@/components/LinkButton";

import { IVideoSection } from "./types";

type IProps = IVideoSection & JSX.IntrinsicElements["div"];

export const VideoSection: React.FC<IProps> = ({
  title,
  subtitle,
  media,
  videoOnTheRight = true,
  hasBackground = false,
  cta,
  ...rest
}) => {
  const { css } = useFela();
  const { colors, bp } = useTheme();
  const { ref, inView } = useInView();

  const background = hasBackground
    ? "url('/svgs/bubble-cut.svg') no-repeat 100% 48%"
    : "";

  return (
    <Grid
      id="VideoSection"
      customStyle={{
        flexDirection: videoOnTheRight ? "column" : "column-reverse",
        alignItems: "center",
        [bp.FROM_DESKTOP]: {
          flexDirection: videoOnTheRight ? "row" : "row-reverse",
          justifyContent: "space-between",
        },
      }}
      wrapperStyle={{ background }}
      {...rest}
    >
      <div
        className={css({
          textAlign: "center",
          [bp.FROM_DESKTOP]: { maxWidth: 420, textAlign: "left" },
        })}
      >
        <Heading as="h2">{title}</Heading>

        <p
          className={css({
            fontSize: 18,
            marginTop: 15,
            letterSpacing: 0.5,
            marginBottom: 26,
            color: colors.GRAY,
            lineHeight: "28px",
            nested: {
              "> strong": {
                color: colors.SKY_BLUE,
              },
            },
            [bp.FROM_DESKTOP]: {
              marginTop: 30,
            },
          })}
        >
          {subtitle}
        </p>
        {cta && <LinkButton customStyle={{ marginTop: 45 }} {...cta} />}
      </div>

      {media && (
        <Card
          customStyle={{
            flex: 0,
            padding: 10,
            alignItems: "center",
            marginTop: videoOnTheRight ? 30 : 0,
            marginBottom: videoOnTheRight ? 0 : 30,
            [bp.FROM_TABLET]: {
              padding: 20,
              marginTop: videoOnTheRight ? 50 : 0,
              marginBottom: videoOnTheRight ? 0 : 50,
            },
            [bp.FROM_DESKTOP]: {
              margin: 0,
            },
          }}
        >
          {media.type === "VIDEO" && (
            <video
              ref={ref}
              title={media.title}
              src={inView ? media.src : undefined}
              autoPlay
              loop
              muted
              playsInline
              className={css({
                width: "100%",
                maxWidth: "85vw",
                height: "auto",
                margin: "0 auto",
                [bp.FROM_TABLET]: {
                  width: 530,
                  maxWidth: "none",
                },
              })}
            >
              {media.title}
            </video>
          )}

          {media.type === "PHOTO" && (
            <img
              title={media.title}
              alt={media.title}
              ref={ref}
              src={inView ? media.src : undefined}
              className={css({
                width: "100%",
                maxWidth: "85vw",
                height: "auto",
                [bp.FROM_TABLET]: {
                  width: 530,
                  maxWidth: "none",
                },
              })}
            />
          )}
        </Card>
      )}
    </Grid>
  );
};
